import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, ArrowRight, Sparkles, MapPin, Video, FileText } from "lucide-react";
import { format, parseISO } from "date-fns";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

function EventRow({ e }) {
  let dateLabel = "";
  try { if (e.start_at) dateLabel = format(parseISO(e.start_at), "MMM d · h:mm a"); } catch (err) { /* noop */ }
  const virtual = e.format === "virtual" || (!!e.online_url && !(e.location?.city || e.location?.state));
  const loc = virtual ? "Virtual" : [e.location?.city, e.location?.state].filter(Boolean).join(", ");
  return (
    <Link to={e.slug ? `/event/${e.slug}` : "/events"} className="-mx-2 block rounded-lg p-2 transition-colors hover:bg-secondary/60" data-testid={`chatter-event-${e.id}`}>
      <div className="line-clamp-2 text-sm font-[560] text-foreground">{e.title}</div>
      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
        {dateLabel && <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" />{dateLabel}</span>}
        {loc && <span className="inline-flex items-center gap-1">{virtual ? <Video className="h-3 w-3" /> : <MapPin className="h-3 w-3" />}{loc}</span>}
      </div>
    </Link>
  );
}

function ResourceRow({ r }) {
  return (
    <Link to={r.slug ? `/resource/${r.slug}` : "/resources"} className="-mx-2 block rounded-lg p-2 transition-colors hover:bg-secondary/60" data-testid={`chatter-resource-${r.id}`}>
      <div className="line-clamp-2 text-sm font-[560] text-foreground">{r.title}</div>
      {r.resource_type && <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground"><FileText className="h-3 w-3" /><span className="capitalize">{String(r.resource_type).replace(/[-_]/g, " ")}</span></div>}
    </Link>
  );
}

export function ChatterSidebar() {
  const [events, setEvents] = useState(null);
  const [resources, setResources] = useState(null);

  useEffect(() => {
    api.get("/events/public", { params: { when: "upcoming", limit: 5 } }).then(({ data }) => setEvents(data || [])).catch(() => setEvents([]));
    api.get("/resources/public", { params: { sort: "recent", limit: 5 } }).then(({ data }) => setResources(data || [])).catch(() => setResources([]));
  }, []);

  return (
    <div className="space-y-5">
      <Card className="border-0 p-5" data-testid="chatter-events-card">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-[680] uppercase tracking-wide text-primary">Upcoming events</h3>
          <Link to="/events" className="text-xs font-[560] text-primary hover:underline" data-testid="chatter-events-viewall">View all</Link>
        </div>
        <div className="mt-3 space-y-1">
          {events === null ? <p className="text-sm text-muted-foreground">Loading…</p>
            : events.length === 0 ? <p className="text-sm text-muted-foreground">No upcoming events.</p>
            : events.map((e) => <EventRow key={e.id} e={e} />)}
        </div>
      </Card>

      <Card className="border-0 p-5" data-testid="chatter-resources-card">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-[680] uppercase tracking-wide text-primary">Resources</h3>
          <Link to="/resources" className="text-xs font-[560] text-primary hover:underline" data-testid="chatter-resources-viewall">View all</Link>
        </div>
        <div className="mt-3 space-y-1">
          {resources === null ? <p className="text-sm text-muted-foreground">Loading…</p>
            : resources.length === 0 ? <p className="text-sm text-muted-foreground">No resources yet.</p>
            : resources.map((r) => <ResourceRow key={r.id} r={r} />)}
        </div>
      </Card>

      <Card className="border border-dashed border-border/70 bg-secondary/30 p-5 text-center" data-testid="chatter-ad-placeholder">
        <div className="mx-auto flex h-9 w-9 items-center justify-center rounded-full bg-secondary text-muted-foreground"><Sparkles className="h-4 w-4" /></div>
        <div className="mt-2 text-[11px] font-[680] uppercase tracking-wide text-muted-foreground">Partner Spotlight</div>
        <p className="mt-1 text-xs text-muted-foreground">Your brand could be here. Reach the Startup Otter community.</p>
      </Card>
    </div>
  );
}

export default ChatterSidebar;
